import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const SpanishColonialADUDesignFremont = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Spanish Colonial ADU Design & Construction in Fremont",
    "description": "Custom Spanish Colonial ADUs in Fremont. Expert design & construction by UDA ADU. Authentic Spanish colonial architecture with modern amenities.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU"
    },
    "url": "https://www.udaadu.com/spanish-colonial-adu-design-fremont"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/spanish-colonial-adu-design-fremont" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] relative">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu/30min"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Spanish Colonial ADU Design in Fremont
          </h1>
          <p className="text-xl text-gray-200 max-w-4xl mx-auto mb-8">
            Embrace California's rich architectural heritage with a stunning Spanish Colonial ADU in Fremont. Our authentic designs capture the romance and elegance of Old California while incorporating modern conveniences for contemporary living.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Schedule Design Consultation
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/landing">Try UDA AI Designer</Link>
            </Button>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-foreground">
              Authentic Spanish Colonial Features
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Red Clay Tile Roofing</h3>
                  <p className="text-muted-foreground">Traditional barrel tiles in warm terra cotta tones</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Stucco Exterior Walls</h3>
                  <p className="text-muted-foreground">Smooth or textured stucco in cream, white, or earth tones</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Arched Openings</h3>
                  <p className="text-muted-foreground">Graceful arches for windows, doors, and architectural details</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Wrought Iron Details</h3>
                  <p className="text-muted-foreground">Decorative ironwork on windows, balconettes, and gates</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Courtyards & Patios</h3>
                  <p className="text-muted-foreground">Private outdoor spaces perfect for California living</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-foreground">
              Spanish Colonial Benefits in Fremont
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Climate Compatibility</h3>
                <p className="text-muted-foreground">Spanish Colonial design naturally suits Fremont's Mediterranean-like climate</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">California Heritage</h3>
                <p className="text-muted-foreground">Celebrates the state's architectural history and cultural identity</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Energy Efficiency</h3>
                <p className="text-muted-foreground">Thick walls and strategic orientation provide natural cooling</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Distinctive Character</h3>
                <p className="text-muted-foreground">Creates a unique, memorable property that stands out in the market</p>
              </div>
            </div>
          </div>
        </div>

        {/* Interior Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">
            Spanish Colonial Interior Elements
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Tile Flooring</h3>
              <p className="text-muted-foreground">Handcrafted ceramic or saltillo tiles in warm earth tones</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Exposed Wood Beams</h3>
              <p className="text-muted-foreground">Dark-stained vigas and corbels for authentic architectural character</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Arched Niches</h3>
              <p className="text-muted-foreground">Built-in display areas and storage with graceful curved tops</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Decorative Tiles</h3>
              <p className="text-muted-foreground">Hand-painted Talavera or Moorish-inspired tile accents</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Warm Color Palette</h3>
              <p className="text-muted-foreground">Rich oranges, deep reds, golden yellows, and earth tones</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Rustic Hardware</h3>
              <p className="text-muted-foreground">Hand-forged iron handles, hinges, and decorative elements</p>
            </div>
          </div>
        </div>

        {/* Outdoor Living */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">
            Spanish Colonial Outdoor Living
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-muted rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Private Courtyards</h3>
              <p className="text-muted-foreground mb-4">Central outdoor rooms that bring light and air into the heart of your ADU.</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Fountain or water feature centerpiece</li>
                <li>• Lush landscaping with Mediterranean plants</li>
                <li>• Natural extension of indoor living spaces</li>
              </ul>
            </div>
            <div className="p-6 bg-muted rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Covered Loggia</h3>
              <p className="text-muted-foreground mb-4">Shaded outdoor corridors perfect for relaxation and entertaining.</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Cool retreat from California sun</li>
                <li>• Elegant arched openings</li>
                <li>• Ideal for outdoor dining and lounging</li>
              </ul>
            </div>
            <div className="p-6 bg-muted rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Terracotta Terraces</h3>
              <p className="text-muted-foreground mb-4">Multi-level outdoor spaces with authentic clay tile surfaces.</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Durable, low-maintenance surfaces</li>
                <li>• Beautiful aging and patina development</li>
                <li>• Perfect for container gardens</li>
              </ul>
            </div>
            <div className="p-6 bg-muted rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Garden Integration</h3>
              <p className="text-muted-foreground mb-4">Seamless blending of architecture with landscape design.</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Native California and Mediterranean plants</li>
                <li>• Drought-tolerant landscaping</li>
                <li>• Aromatic herbs and citrus trees</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">
            Fremont Spanish Colonial ADU Process
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Heritage Consultation</h3>
              <p className="text-muted-foreground">Explore authentic Spanish Colonial design for your Fremont site</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Authentic Design</h3>
              <p className="text-muted-foreground">Create historically accurate plans with modern functionality</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Permit Navigation</h3>
              <p className="text-muted-foreground">Handle Fremont building codes and design review requirements</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Artisan Construction</h3>
              <p className="text-muted-foreground">Traditional techniques with modern building standards</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-muted rounded-lg p-8 text-center">
          <h2 className="text-3xl font-semibold mb-4 text-foreground">
            Build Your Spanish Colonial Dream in Fremont
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Create an authentic Spanish Colonial ADU that celebrates California's architectural heritage while providing modern comfort and functionality. Our Fremont specialists are ready to bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Start Your Colonial Project
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/adu-calculator">Get Spanish Colonial Quote</Link>
            </Button>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-4 text-foreground">Explore More Fremont ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/mediterranean-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Mediterranean ADUs Fremont
            </Link>
            <Link to="/contemporary-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Contemporary ADUs Fremont
            </Link>
            <Link to="/craftsman-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Craftsman ADUs Fremont
            </Link>
            <Link to="/transitional-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Transitional ADUs Fremont
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default SpanishColonialADUDesignFremont;